import { Center, Spinner } from "@chakra-ui/react";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import AdminRoute from "./components/AdminRoute";
import { useAuth } from "./hooks/useAuth";
import MainLayout from "./layouts/MainLayout";
import Login from "./pages/Login";

const NewsPage = lazy(() => import("./pages/NewsPage"));
const NewsDetailPage = lazy(() => import("./pages/NewsDetailPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const LibraryPage = lazy(() => import("./pages/LibraryPage"));
const AdminNewsPage = lazy(() => import("./pages/AdminNewsPage"));
const AdminNewsEditorPage = lazy(
    () => import("./pages/AdminNewsEditorPage"),
);
const AdminNewsPreviewPage = lazy(
    () => import("./pages/AdminNewsPreviewPage"),
);

const AuthLoadingScreen = () => (
    <Center minH="100vh" role="status" aria-label="Loading account">
        <Spinner size="xl" />
    </Center>
);

const protectedPage = (page: React.ReactElement) => (
    <ProtectedRoute>
        <MainLayout>{page}</MainLayout>
    </ProtectedRoute>
);

const adminPage = (page: React.ReactElement) =>
    protectedPage(<AdminRoute>{page}</AdminRoute>);

const App = () => {
    const { user, isInitializing } = useAuth();

    if (isInitializing) {
        return <AuthLoadingScreen />;
    }

    return (
        <>
            <ScrollToTop />
            <Suspense fallback={<AuthLoadingScreen />}>
                <Routes>
            <Route
                path="/login"
                element={user ? <Navigate to="/" replace /> : <Login />}
            />
            <Route path="/" element={protectedPage(<NewsPage />)} />
            <Route
                path="/news/:id"
                element={protectedPage(<NewsDetailPage />)}
            />
            <Route
                path="/profile"
                element={protectedPage(<ProfilePage />)}
            />
            <Route
                path="/saved"
                element={protectedPage(<LibraryPage mode="saved" />)}
            />
            <Route
                path="/history"
                element={protectedPage(<LibraryPage mode="history" />)}
            />
            <Route
                path="/admin/news"
                element={adminPage(<AdminNewsPage />)}
            />
            <Route
                path="/admin/news/new"
                element={adminPage(<AdminNewsEditorPage />)}
            />
            <Route
                path="/admin/news/:id/edit"
                element={adminPage(<AdminNewsEditorPage />)}
            />
            <Route
                path="/admin/news/:id/preview"
                element={adminPage(<AdminNewsPreviewPage />)}
            />
            <Route
                path="/pricing"
                element={
                    <MainLayout>
                        <PricingPage />
                    </MainLayout>
                }
            />
            <Route
                path="*"
                element={
                    <MainLayout>
                        <NotFoundPage />
                    </MainLayout>
                }
            />
                </Routes>
            </Suspense>
        </>
    );
};

export default App;
